
export const generateJobDescriptionEmail = (job: {
  title: string;
  company: {
    id: string;
    name: string;
  };
  skills: string[];
  description: string;
}) => {
  const subject = `Job Offer: ${job.title} at ${job.company.name}`;
  
  // Create a basic HTML version of the job description for the email body
  const htmlContent = `
    <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; color: #333; }
          .header { color: #0E2356; font-size: 20px; font-weight: bold; margin-bottom: 10px; }
          .company { color: #233772; font-size: 16px; margin-bottom: 20px; }
          .section { margin-bottom: 15px; }
          .section-title { font-weight: bold; margin-bottom: 5px; }
          .skills { display: flex; flex-wrap: wrap; }
          .skill { background: #f0f4ff; border-radius: 4px; padding: 4px 8px; margin: 2px; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="header">${job.title}</div>
        <div class="company">${job.company.name}</div>
        
        <div class="section">
          <div class="section-title">Job Description:</div>
          <p>${job.description || 'No description provided.'}</p>
        </div>
        
        <div class="section">
          <div class="section-title">Responsibilities:</div>
          <ul>
            <li>Gather requirements and transform them into functional specifications.</li>
            <li>Collaborate with cross-functional teams including product managers, architects, developers, and security officers.</li>
            <li>Write technical documentation, release notes, and user guides.</li>
            <li>Participate in Agile ceremonies (demos, refinement meetings, planning, retrospectives).</li>
            <li>Support developers during the full life cycle, validate their design, check their deliveries.</li>
            <li>Ensure solutions align with customer needs and business goals.</li>
          </ul>
        </div>
        
        <div class="section">
          <div class="section-title">Requirements:</div>
          <ul>
            <li>5+ years of relevant experience in ${job.title} or similar roles.</li>
            <li>Strong expertise in industry standards and best practices.</li>
            <li>Experience working in Agile environments.</li>
            <li>Excellent problem-solving skills and attention to detail.</li>
            <li>Fluent in English AND Dutch or French.</li>
          </ul>
        </div>
        
        ${job.skills && job.skills.length > 0 ? `
        <div class="section">
          <div class="section-title">Skills:</div>
          <div class="skills">
            ${job.skills.map(skill => `<div class="skill">${skill}</div>`).join('')}
          </div>
        </div>
        ` : ''}
      </body>
    </html>
  `;
  
  // Create mailto link with body encoded
  const mailtoBody = `
Job Offer: ${job.title}
Company: ${job.company.name}

Job Description:
${job.description || 'No description provided.'}

Skills:
${job.skills ? job.skills.join(', ') : 'None specified'}

For more details, please see the attached PDF.
  `;
  
  const encodedBody = encodeURIComponent(mailtoBody);
  const encodedSubject = encodeURIComponent(subject);
  
  return {
    subject,
    htmlContent,
    mailtoLink: `mailto:?subject=${encodedSubject}&body=${encodedBody}`
  };
};

// Function to create a data URL for the PDF
export const createJobDescriptionPDF = async (job: {
  title: string;
  company: {
    id: string;
    name: string;
  };
  skills: string[];
  description: string;
}): Promise<string> => {
  // In a real implementation, we would use a PDF generation library like jspdf
  // Since we can't install new packages, we'll create a simple HTML page that looks like a PDF
  // and use the browser's print functionality
  
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${job.title} - Job Description</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          color: #333;
          margin: 0;
          padding: 20px;
          max-width: 800px;
          margin: 0 auto;
        }
        .header {
          text-align: center;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 1px solid #eee;
        }
        .company-logo {
          color: #0E2356;
          font-size: 24px;
          font-weight: bold;
          margin-bottom: 5px;
        }
        .job-title {
          font-size: 28px;
          font-weight: bold;
          color: #0E2356;
          margin: 10px 0;
        }
        .company-name {
          font-size: 20px;
          color: #233772;
        }
        .section {
          margin-bottom: 20px;
        }
        .section-title {
          font-size: 18px;
          font-weight: bold;
          color: #0E2356;
          margin-bottom: 10px;
        }
        .description {
          line-height: 1.6;
        }
        .skills-container {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 10px;
        }
        .skill {
          background-color: #f0f4ff;
          color: #0E2356;
          padding: 6px 12px;
          border-radius: 16px;
          font-size: 14px;
        }
        ul {
          padding-left: 20px;
        }
        li {
          margin-bottom: 8px;
        }
        .footer {
          margin-top: 40px;
          text-align: center;
          font-size: 12px;
          color: #666;
          border-top: 1px solid #eee;
          padding-top: 20px;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="company-logo">DIGGER</div>
        <div class="job-title">${job.title}</div>
        <div class="company-name">${job.company.name}</div>
      </div>
      
      <div class="section">
        <div class="section-title">Job Description</div>
        <div class="description">
          ${job.description || 'No description provided.'}
        </div>
      </div>
      
      <div class="section">
        <div class="section-title">Responsibilities</div>
        <ul>
          <li>Gather requirements and transform them into functional specifications.</li>
          <li>Collaborate with cross-functional teams including product managers, architects, developers, and security officers.</li>
          <li>Write technical documentation, release notes, and user guides.</li>
          <li>Participate in Agile ceremonies (demos, refinement meetings, planning, retrospectives).</li>
          <li>Support developers during the full life cycle, validate their design, check their deliveries.</li>
          <li>Ensure solutions align with customer needs and business goals.</li>
          <li>Contribute to continuous improvement in product development, tooling, and methodologies.</li>
        </ul>
      </div>
      
      <div class="section">
        <div class="section-title">Requirements</div>
        <ul>
          <li>5+ years of relevant experience in ${job.title} or similar roles.</li>
          <li>Strong expertise in industry standards and best practices.</li>
          <li>Experience working in Agile environments.</li>
          <li>Excellent problem-solving skills and attention to detail.</li>
          <li>Fluent in English AND Dutch or French.</li>
        </ul>
      </div>
      
      ${job.skills && job.skills.length > 0 ? `
      <div class="section">
        <div class="section-title">Skills</div>
        <div class="skills-container">
          ${job.skills.map(skill => `<div class="skill">${skill}</div>`).join('')}
        </div>
      </div>
      ` : ''}
      
      <div class="footer">
        <p>This job description was generated by Digger Recruiting Platform.</p>
        <p>© ${new Date().getFullYear()} Digger - All Rights Reserved</p>
      </div>
    </body>
    </html>
  `;
  
  // In a real application with the ability to install packages,
  // we would convert this HTML to PDF using a library.
  // For demo purposes, we'll just return the HTML content.
  return htmlContent;
};
